<script lang="ts">
/**
 * @component TabBar
 *
 * iOS 26 Liquid Glass pill tab bar for mobile navigation. Renders a floating,
 * pill-shaped bottom bar with translucent glass material containing 5 tabs:
 * Home, About Me, Projects, CV, and More. The "More" tab opens the sidebar
 * drawer for access to additional navigation items.
 *
 * Shown only on mobile viewports (below md/768px breakpoint) and hidden when
 * the sidebar drawer is already open. Uses the project's Liquid Glass material
 * system (my-glass) with safe area inset padding for notch/Dynamic Island.
 *
 * The active tab gets a pill-shaped highlight capsule behind it. All touch
 * targets meet Apple's 44pt minimum requirement.
 *
 * Built on shadcn-svelte NavigationMenu primitives.
 *
 * @see {@link AppSidebar} -- Full sidebar opened by the "More" tab
 * @see {@link NavBar} -- Desktop-only fallback header (complementary to TabBar)
 */
import { page } from "$app/state";
import { useSidebar } from "$lib/components/ui/sidebar";
import { t, getLocalizedUrl, isActive, locale } from "$lib/i18n";
import { type LocaleCode } from "$lib/i18n/helpers";
import * as NavigationMenu from "$lib/components/ui/navigation-menu/index.js";
import { House, User, FolderKanban, FileText, Ellipsis } from "@lucide/svelte";
import { cn } from "$lib/utils";
import { createWebHaptics } from "web-haptics/svelte";
import { onDestroy } from "svelte";

const { trigger, destroy } = createWebHaptics();
onDestroy(destroy);

const sidebar = useSidebar();

const hapticPattern = [
  { duration: 60, intensity: 1 },
  { delay: 30, duration: 60, intensity: 0.75 },
  { delay: 30, duration: 60 },
  { delay: 30, duration: 60, intensity: 0.75 },
  { delay: 30, duration: 60, intensity: 1 },
];

interface TabItem {
  title: string;
  url: string;
  url2?: string;
  icon: any;
}

let tabs: TabItem[] = $derived([
  { title: $t("sites.root"), url: "/", icon: House },
  { title: $t("sites.aboutme"), url: "/aboutme", icon: User },
  { title: $t("sites.projects"), url: "/projects", icon: FolderKanban },
  { title: $t("sites.cv"), url: "/cv", url2: "/cv/ats", icon: FileText },
]);

function localizedHref(url: string): string {
  return getLocalizedUrl(url, $locale as LocaleCode);
}

/** Whether any of the sidebar-only routes (Contact, Linkhub, Imprint) are active */
let moreIsActive: boolean = $derived(
  ["/contact", "/linkhub", "/imprint"].some((url) => {
    const currentLoc = $locale as LocaleCode;
    const path = page.url.pathname.replace(/\/$/, "") || "/";
    const localized =
      getLocalizedUrl(url, currentLoc).replace(/\/$/, "") || "/";
    return path === localized;
  }),
);
</script>

<!-- Bottom-docked TabBar container with safe-area support -->
<nav
  class={cn(
    "fixed bottom-0 left-0 right-0 z-40 w-full",
    "flex justify-center",
    "pointer-events-none",
    "transition-transform duration-300 ease-out",
    "md:hidden xl:hidden",
    "print:hidden no-print",
    sidebar.openMobile && "translate-y-full",
  )}
>
  <NavigationMenu.Root
    viewport={false}
    class="pointer-events-auto my-glass w-full max-w-none rounded-t-xl rounded-b-none border-t border-border/40 px-2 pt-2"
    style="padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 8px);"
    aria-label="Main navigation"
  >
    <NavigationMenu.List class="flex items-center justify-around w-full max-w-lg mx-auto gap-1">
      {#each tabs as tab}
        <NavigationMenu.Item>
          <NavigationMenu.Link
            href={localizedHref(tab.url)}
            data-active={isActive($locale as LocaleCode, tab.url, tab.url2)}
            class={cn(
              "relative flex flex-col items-center justify-center gap-0.5 rounded-sm px-2 py-2 min-h-11 min-w-11",
              "bg-transparent transition-all duration-200",
              "hover:bg-card/20 focus:bg-card/20",
              isActive($locale as LocaleCode, tab.url, tab.url2)
                ? "bg-primary/15 text-primary data-[active=true]:bg-primary/15 data-[active=true]:hover:bg-primary/20 data-[active=true]:focus:bg-primary/20"
                : "text-muted-foreground",
            )}
            onclick={() => {
              try {
                trigger(hapticPattern);
              } catch {}
            }}
          >
            <tab.icon class="size-5" />
            <span class="text-[10px] leading-tight font-medium"
              >{tab.title}</span
            >
          </NavigationMenu.Link>
        </NavigationMenu.Item>
      {/each}

      <!-- More tab (opens sidebar drawer) -->
      <NavigationMenu.Item>
        <button
          data-slot="navigation-menu-link"
          onclick={() => {
            sidebar.setOpenMobile(true);
            try {
              trigger(hapticPattern);
            } catch {}
          }}
          class={cn(
            "relative flex flex-col items-center justify-center gap-0.5 rounded-sm px-4 py-2 min-h-11 min-w-11",
            "bg-transparent",
            moreIsActive
              ? "bg-primary/15 text-primary"
              : "text-muted-foreground hover:bg-card/20",
          )}
          aria-label={$t("sidebar.toggleSidebar")}
        >
          <Ellipsis class="size-5" />
          <span class="text-[10px] leading-tight font-medium">More</span>
        </button>
      </NavigationMenu.Item>
    </NavigationMenu.List>
  </NavigationMenu.Root>
</nav>
